import type { Translations } from "@/lib/i18n/types";
import type { BillingContext, BillingOverview, BillingPlan, BuildingPlanTier } from "./types";
import { isAutomaticPaymentMethod } from "./types";

type BuildingCostQuote = BillingOverview["add_building_quote"];

export function buildingQuoteTimingCopy(quote: BuildingCostQuote, t: Translations): string {
  if (quote.charge_type === "full_month") return t.billing.consent.quoteFullMonth;
  if (quote.charge_timing === "immediate") return t.billing.consent.quoteDetail;
  if (quote.charge_timing === "next_invoice") return t.billing.consent.quoteNextInvoice;
  return t.billing.consent.quoteTimingUnknown;
}

export function requiresAutomaticPaymentSetup(context: BillingContext): boolean {
  return (
    context.allows_legacy_building_creation === false &&
    !isAutomaticPaymentMethod(context.details.payment_method)
  );
}

export function paymentMethodForBillingSetup(context: BillingContext) {
  return requiresAutomaticPaymentSetup(context)
    ? "card"
    : (context.details.payment_method ?? "card");
}

function paidBuildingReadiness(context: BillingContext) {
  return {
    detailsMissing: context.paid_details_missing ?? context.details_missing,
    paymentMethodMissing:
      requiresAutomaticPaymentSetup(context) ||
      (context.paid_payment_method_missing ?? context.payment_method_missing),
  };
}

export function quoteForBuildingPlan(
  quote: BuildingCostQuote,
  plan?: BillingPlan
): BuildingCostQuote {
  if (!plan) return quote;
  const remaining = quote.billable_days;
  const duration = quote.period_days;
  const amount =
    quote.charge_type !== "full_month" && duration > 0
      ? Math.round((plan.rate_cents * Math.min(remaining, duration)) / duration)
      : plan.rate_cents;
  return { ...quote, monthly_rate_cents: plan.rate_cents, amount_cents: amount };
}

export function paidBuildingCreationBlock(
  context: BillingContext,
  tier: BuildingPlanTier,
  t: Translations
): string | undefined {
  if (tier === "free") return undefined;
  if (context.is_read_only) return t.billing.restrictions.addReadOnly;
  const readiness = paidBuildingReadiness(context);
  if (readiness.detailsMissing) return t.billing.restrictions.addDetailsMissing;
  if (readiness.paymentMethodMissing) return t.billing.restrictions.addPaymentMethodMissing;
  return undefined;
}

export function paidBuildingSetupNeeded(context: BillingContext, tier: BuildingPlanTier): boolean {
  const readiness = paidBuildingReadiness(context);
  return (
    context.is_org_owner &&
    !context.is_read_only &&
    tier !== "free" &&
    Boolean(readiness.detailsMissing || readiness.paymentMethodMissing)
  );
}
